import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { logoutUser } from '@/features/auth/authThunks';
import type { RootState } from '@/store/root-state';
import type { InvitationsState } from './invitationTypes';
import {
  acceptInvitation,
  createInvitation,
  deleteInvitation,
  fetchInvitations,
  getInvitation,
  hardDeleteInvitation,
  resendInvitation,
  verifyInvitationToken,
} from './invitationsThunks';

const initialState: InvitationsState = {
  items: [],
  isLoading: false,
  isFetching: false,
  activeToken: null,
  activeInvitation: null,
  activeInvitationPhase: 'idle',
  activeInvitationErrorReason: null,
  activeInvitationErrorMessage: null,
};

function upsertInvitation(state: InvitationsState, invitation: InvitationsState['items'][number]) {
  const exactIndex = state.items.findIndex((item) => item.id === invitation.id);
  if (exactIndex !== -1) {
    state.items[exactIndex] = invitation;
    return;
  }

  // Some invitation mutations may return a new record ID for the same pending invite.
  const fallbackIndex = state.items.findIndex(
    (item) =>
      item.email === invitation.email && item.organizationID === invitation.organizationID && item.status === 'pending',
  );

  if (fallbackIndex !== -1) {
    state.items[fallbackIndex] = invitation;
    return;
  }

  state.items.push(invitation);
}

const invitationsSlice = createSlice({
  name: 'invitations',
  initialState,
  reducers: {
    setActiveInvitationToken: (state, action: PayloadAction<string | null>) => {
      const nextToken = action.payload?.trim() || null;
      if (state.activeToken === nextToken) return;

      state.activeToken = nextToken;
      state.activeInvitation = null;
      state.activeInvitationPhase = nextToken ? 'idle' : 'error';
      state.activeInvitationErrorReason = nextToken ? null : 'not_found';
      state.activeInvitationErrorMessage = null;
    },
    clearActiveInvitationContext: (state) => {
      state.activeToken = null;
      state.activeInvitation = null;
      state.activeInvitationPhase = 'idle';
      state.activeInvitationErrorReason = null;
      state.activeInvitationErrorMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvitations.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchInvitations.fulfilled, (state, action) => {
        state.isFetching = false;
        state.items = action.payload;
      })
      .addCase(fetchInvitations.rejected, (state) => {
        state.isFetching = false;
      })
      .addCase(getInvitation.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(getInvitation.fulfilled, (state, action) => {
        state.isFetching = false;
        upsertInvitation(state, action.payload);
      })
      .addCase(getInvitation.rejected, (state) => {
        state.isFetching = false;
      })
      .addCase(verifyInvitationToken.pending, (state, action) => {
        state.activeToken = action.meta.arg;
        state.activeInvitation = null;
        state.activeInvitationPhase = 'verifying';
        state.activeInvitationErrorReason = null;
        state.activeInvitationErrorMessage = null;
      })
      .addCase(verifyInvitationToken.fulfilled, (state, action) => {
        state.activeToken = action.meta.arg;
        if (action.payload.valid) {
          state.activeInvitation = action.payload;
          state.activeInvitationPhase = 'ready';
          state.activeInvitationErrorReason = null;
          state.activeInvitationErrorMessage = null;
          return;
        }

        state.activeInvitation = null;
        state.activeInvitationPhase = 'error';
        state.activeInvitationErrorReason = action.payload.reason || 'not_found';
        state.activeInvitationErrorMessage = action.payload.message || null;
      })
      .addCase(verifyInvitationToken.rejected, (state, action) => {
        state.activeToken = action.meta.arg;
        state.activeInvitation = null;
        state.activeInvitationPhase = 'error';
        state.activeInvitationErrorReason = 'not_found';
        state.activeInvitationErrorMessage = null;
      })
      .addCase(createInvitation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createInvitation.fulfilled, (state, action) => {
        state.isLoading = false;
        upsertInvitation(state, action.payload.invitation);
      })
      .addCase(createInvitation.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteInvitation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteInvitation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = state.items.filter((invitation) => invitation.id !== action.payload);
      })
      .addCase(deleteInvitation.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(hardDeleteInvitation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(hardDeleteInvitation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = state.items.filter((invitation) => invitation.id !== action.payload);
      })
      .addCase(hardDeleteInvitation.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(resendInvitation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(resendInvitation.fulfilled, (state, action) => {
        state.isLoading = false;
        upsertInvitation(state, action.payload.invitation);
      })
      .addCase(resendInvitation.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(acceptInvitation.fulfilled, (state) => {
        state.activeToken = null;
        state.activeInvitation = null;
        state.activeInvitationPhase = 'idle';
        state.activeInvitationErrorReason = null;
        state.activeInvitationErrorMessage = null;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.items = [];
        state.isLoading = false;
        state.isFetching = false;
      });
  },
});

export default invitationsSlice.reducer;
export const { setActiveInvitationToken, clearActiveInvitationContext } = invitationsSlice.actions;

const selectInvitationsState = (state: RootState) => state.invitations;

export const selectInvitations = createSelector(selectInvitationsState, (state) => state.items);
export const selectInvitationsLoading = createSelector(selectInvitationsState, (state) => state.isLoading);
export const selectInvitationsFetching = createSelector(selectInvitationsState, (state) => state.isFetching);
export const selectActiveInvitationToken = createSelector(selectInvitationsState, (state) => state.activeToken);
export const selectActiveInvitation = createSelector(selectInvitationsState, (state) => state.activeInvitation);
export const selectActiveInvitationPhase = createSelector(
  selectInvitationsState,
  (state) => state.activeInvitationPhase,
);
export const selectActiveInvitationErrorReason = createSelector(
  selectInvitationsState,
  (state) => state.activeInvitationErrorReason,
);
export const selectActiveInvitationErrorMessage = createSelector(
  selectInvitationsState,
  (state) => state.activeInvitationErrorMessage,
);
